import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import type {FeatureFlagApiPayload, FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';
import {DeepPartial} from 'common/util/typescript';

interface AppState {
  featureFlags: FeatureFlagApiResponse[];
  newFeatureFlag?: DeepPartial<FeatureFlagApiPayload>;
  setFeatureFlagList: (flags: FeatureFlagApiResponse[]) => void;
  setNewFeatureFlag: (flag?: DeepPartial<FeatureFlagApiPayload>) => void;
  canSaveNewFeatureFlag: () => boolean;
  currentFeatureFlag?: FeatureFlagApiResponse;
  setCurrentFeatureFlag: (flag: FeatureFlagApiResponse) => void;
  isEditModalOpen: boolean;
  setEditModalOpen: (isOpen: boolean) => void;
}

/**
 * Determines if a flag is fully configured to be sent to the API.
 */
export const isFeatureFlagApiResponse = (flag: any): flag is FeatureFlagApiResponse => {
  return !!(
    flag?.name &&
    flag?.key &&
    flag?.context &&
    flag?.ruleset &&
    flag?.serving_method &&
    Array.isArray(flag?.tags)
  );
};

export const useFeatureFlagStore = create<AppState>()(
  devtools((set, get) => ({
    featureFlags: [],
    setFeatureFlagList: (flags) => set({featureFlags: flags}),
    setNewFeatureFlag: (flag) => {
      if (!flag) {
        set({
          newFeatureFlag: {
            tags: [],
            ruleset: {
              variants: [],
              rollout: [],
            },
          },
        });
      } else {
        set({newFeatureFlag: flag});
      }
    },
    canSaveNewFeatureFlag: () => {
      const flag = get().newFeatureFlag;
      return isFeatureFlagApiResponse(flag);
    },
    setCurrentFeatureFlag: (flag) => set({currentFeatureFlag: flag}),
    isEditModalOpen: false,
    setEditModalOpen: (isOpen) => set({isEditModalOpen: isOpen}),
  })),
);
